import { Component, HostListener, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'src/app/Services/global.service';
import { getLocalJSON } from '../../../_helpers/storage.util';
declare var $: any;

@Component({
  selector: 'app-bank-account-setup',
  templateUrl: './bank-account-setup.component.html',
  styleUrls: ['./bank-account-setup.component.scss'],
})
export class BankAccountSetupComponent implements OnInit {
  /** When true, renders only the setup cards (no header/sidebar page shell). */
  @Input() embedded = false;

  public loading = false;
  public loginDetail: any = {};
  public currentDetail: any = {};
  public stripeDetail: any = {};
  public imgURL: any = '';
  public countryList: any[] = [];
  public selectedCountryId = '';
  public selectedCountry: any = null;
  public dropdownOpen = false;
  public searchTerm = '';

  /** Only Australia (AUD) is supported for Stripe payouts currently. */
  private readonly supportedCountryCodes = ['AU'];
  private stripeWindowOpened = false;

  constructor(public router: Router, public service: GlobalService) {
    this.loginDetail = getLocalJSON('grw_login', {});
    this.currentDetail = this.loginDetail;
    this.imgURL = this.loginDetail?.profile_image || '';
    this.stripeDetail = this.loginDetail?.stripeDetail || {};
  }

  ngOnInit(): void {
    if (!this.embedded) {
      $('#text23').trigger('click');
    }
    this.getProfile();
    this.getCountry();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible' && this.stripeWindowOpened) {
      this.stripeWindowOpened = false;
      this.getProfile();
    }
  }

  get isStripeLinked(): boolean {
    return (
      this.loginDetail?.stripe_account_verify == true ||
      this.stripeDetail?.detail_submitted == true
    );
  }

  get isCountrySupported(): boolean {
    if (!this.selectedCountry) {
      return false;
    }
    return this.supportedCountryCodes.includes(
      (this.selectedCountry.code || '').toUpperCase()
    );
  }

  get canConnectStripe(): boolean {
    return !this.isStripeLinked && this.isCountrySupported;
  }

  get stripeManageUrl(): string {
    const link = this.stripeDetail?.login_links;
    if (typeof link === 'string' && link && !link.includes('api.stripe.com')) {
      return link;
    }
    return '';
  }

  get alertState(): 'idle' | 'supported' | 'unsupported' | 'linked' {
    if (this.isStripeLinked) {
      return 'linked';
    }
    if (!this.selectedCountry) {
      return 'idle';
    }
    return this.isCountrySupported ? 'supported' : 'unsupported';
  }

  get filteredCountries(): any[] {
    const term = (this.searchTerm || '').trim().toLowerCase();
    if (!term) {
      return this.countryList;
    }
    return this.countryList.filter((c) => {
      const label = `${c.name} ${c.currency} ${this.currencyLabel(c.currency)}`.toLowerCase();
      return label.includes(term);
    });
  }

  getProfile(): void {
    const wasLinked = this.isStripeLinked;
    this.loading = true;
    this.service.GetApi('/profile').subscribe(
      (response: any) => {
        this.loading = false;
        if (response.success == 1) {
          this.applyProfile(response.data, wasLinked);
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  private applyProfile(data: any, wasLinked = this.isStripeLinked): void {
    this.currentDetail = data || {};
    this.imgURL = data?.profile_image;
    this.stripeDetail = data?.stripeDetail || {};
    this.loginDetail = {
      ...this.loginDetail,
      ...data,
      stripe_account_verify: data?.stripe_account_verify == true,
      profile_complete: data?.profile_complete == true,
      subscription_added: data?.subscription_added == true,
      stripeDetail: this.stripeDetail,
    };
    localStorage.setItem('grw_login', JSON.stringify(this.loginDetail));
    if (data?.userCurrency) {
      localStorage.setItem('grw_logincurrency', data.userCurrency);
    }
    if (data?.userCurrencySymbol) {
      localStorage.setItem('grw_currencySymbol', data.userCurrencySymbol);
    }
    this.service.setSubject(this.loginDetail);
    this.applyCountryFromUser();

    if (!wasLinked && this.isStripeLinked && !this.loginDetail.subscription_added) {
      this.router.navigate(['/presentation']);
    }
  }

  getCountry(): void {
    this.service.GetApi('/country').subscribe(
      (response: any) => {
        if (response.success == 1) {
          this.countryList = (response.data || []).filter(
            (c: any) => (c.code || '').toUpperCase() === 'AU'
          );
          this.applyCountryFromUser();
        }
      }
    );
  }

  private applyCountryFromUser(): void {
    if (!this.countryList.length) {
      return;
    }
    const code = (this.stripeDetail?.country || '').toUpperCase();
    if (!code) {
      return;
    }
    const match = this.countryList.find(
      (c) => (c.code || '').toUpperCase() === code
    );
    if (match) {
      this.selectCountry(match, false);
    }
  }

  toggleDropdown(): void {
    if (this.isStripeLinked) {
      return;
    }
    this.dropdownOpen = !this.dropdownOpen;
    if (!this.dropdownOpen) {
      this.searchTerm = '';
    }
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
    this.searchTerm = '';
  }

  selectCountry(country: any, close = true): void {
    if (this.isStripeLinked && close) {
      return;
    }
    this.selectedCountry = country;
    this.selectedCountryId = country?._id || '';
    if (close) {
      this.closeDropdown();
    }
  }

  currencyLabel(code: string): string {
    if (!code) {
      return '';
    }
    try {
      return (
        new Intl.DisplayNames(['en'], { type: 'currency' }).of(
          code.toUpperCase()
        ) || code
      );
    } catch {
      return code;
    }
  }

  flagUrl(code: string): string {
    if (!code) {
      return '';
    }
    return `https://flagcdn.com/24x18/${code.toLowerCase()}.png`;
  }

  connectStripe(): void {
    if (this.isStripeLinked) {
      this.service.warningAlert('Your Stripe account is already connected.');
      return;
    }
    if (!this.isCountrySupported) {
      this.service.warningAlert(
        'Currently, only Australia (AUD) payouts are supported. Please select Australia to connect your Stripe account.'
      );
      return;
    }
    this.loading = true;
    this.service.GetApi('/link-account').subscribe(
      (response: any) => {
        this.loading = false;
        if (response.success == 1) {
          const url = response?.data?.data?.url;
          if (url) {
            this.stripeWindowOpened = true;
            window.open(url, '_blank');
          }
        } else {
          this.service.dangerAlert(response.message || 'Unable to connect Stripe');
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  manageStripe(): void {
    if (!this.isStripeLinked) {
      this.service.warningAlert('Please connect your Stripe account first.');
      return;
    }
    const popup = window.open('', '_blank');
    this.loading = true;
    this.service.GetApi('/manage-account').subscribe(
      (response: any) => {
        this.loading = false;
        const url = response?.data?.url || response?.data?.data?.url || this.stripeManageUrl;
        if (response.success == 1 && url) {
          if (popup) {
            popup.location.href = url;
          } else {
            window.location.href = url;
          }
        } else {
          popup?.close();
          this.service.dangerAlert(response.message || 'Unable to open Stripe dashboard');
        }
      },
      () => {
        this.loading = false;
        popup?.close();
        this.service.dangerAlert('Unable to open Stripe dashboard');
      }
    );
  }
}
